import { useMemo } from 'react';

import { URLS, createJurisdictionHeaders, isDocumentFilled, isMediaFilled, prismicClient } from '@/data';
import { usePrismicStaleTime } from '@/data/prismicHooks';
import { useActiveProductConfig } from '@/hooks/use-active-product';
import { useIsNewArcadePlatform } from '@/hooks/use-is-new-arcade-platform';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SkillGamesConfigDocument } from '@/types/prismic.generated';
import { useQuery } from '@tanstack/react-query';

// types provided by RU https://github.com/betrapp/skill-games-documentation/blob/main/api-container/api-ruc.typings.md
export type PracticeGameMode = 'practice';

export type NonPracticeGameMode = '1v1' | '3v3' | 'ffa' | 'ladder' | 'winner-takes-all';

export type Currency = string;

export type Outcome =
    | {
          result: 'win' | 'draw';
          payout: number;
          currency: Currency;
      }
    | {
          result: 'loss';
      };

export type Stake = {
    stake: number;
    rake: number;
    currency: Currency;
};

export type GameMode = PracticeGameMode | NonPracticeGameMode;

// stakes available per non-practice mode, keyed by mode name
export type ModeStakes = Partial<Record<NonPracticeGameMode, Stake[]>>;

export type Game = {
    game_id: string;
    name: string;
    active: boolean;
    game_modes: GameMode[];
    mode_stakes: ModeStakes;
};

type LegacyGameMode = { mode: NonPracticeGameMode; stakes: Stake[] } | { mode: PracticeGameMode };

type LegacyGame = {
    game_id: string;
    name: string;
    status: string;
    game_modes: LegacyGameMode[];
};

type LegacyGameListResponse = {
    data: LegacyGame[];
};

export type GameWithMetadata = Game & {
    metadata: {
        banner?: string;
        video?: string;
        lobby_image?: string;
        how_to_play_uid?: string;
        card_bg_color?: string;
    };
};

export type GameListResponse = {
    data: Game[];
};

export type SessionResponse =
    | {
          session_id: string;
          stake: Stake;
          game_mode: NonPracticeGameMode;
          expiration: string;
          iframe_url: string;
      }
    | {
          session_id: string;
          game_mode: PracticeGameMode;
          expiration: string;
          iframe_url: string;
      };

export type SessionStateResponse =
    | {
          valid: boolean;
          stake: Stake;
          game_mode: NonPracticeGameMode;
          session_id: string;
          expiration: string;
          iframe_url: string;
      }
    | {
          valid: boolean;
          game_mode: PracticeGameMode;
          expiration: string;
          iframe_url: string;
      };

export type SessionCloseResponse =
    | {
          status: string;
          session_id: string;
          stake: Stake;
          outcome: Outcome;
          settlement_details: {};
      }
    | {
          status: string;
          session_id: string;
      };

export const arcadeApiUrl = (path: string, isNewPlatform: boolean) =>
    `${URLS.ARCADE_GAMES_PROXY_API_URL}${isNewPlatform ? '/v2' : ''}${path}`;

const LEGACY_STAKE_CURRENCIES: Currency[] = ['USD', 'FRE'];

// the legacy platform returned one currency-agnostic stake list and picked the currency at play time,
// so expose a copy per currency to match the shape the Betr Bucks toggle now filters on
const mapLegacyGame = (game: LegacyGame): Game => ({
    game_id: game.game_id,
    name: game.name,
    active: game.status === 'active',
    game_modes: game.game_modes.map(gameMode => gameMode.mode),
    mode_stakes: game.game_modes.reduce<ModeStakes>((acc, gameMode) => {
        if ('stakes' in gameMode) {
            acc[gameMode.mode] = LEGACY_STAKE_CURRENCIES.flatMap(currency =>
                gameMode.stakes.map(stake => ({ ...stake, currency }))
            );
        }
        return acc;
    }, {}),
});

const fetchArcadeGames = async (isNewPlatform: boolean): Promise<GameListResponse> => {
    // TODO: to update API to point to PAM BE when ready
    const response = await fetch(arcadeApiUrl('/auth/games', isNewPlatform), {
        method: 'GET',
        headers: createJurisdictionHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
        throw data;
    }

    if (isNewPlatform) {
        return data as GameListResponse;
    }

    return { ...data, data: ((data as LegacyGameListResponse).data ?? []).map(mapLegacyGame) };
};

const useArcadeGamesQuery = () => {
    const isNewPlatform = useIsNewArcadePlatform();

    return useQuery({
        queryKey: ['arcade-games', isNewPlatform],
        queryFn: () => fetchArcadeGames(isNewPlatform),
    });
};

const stripLegacyPrefix = (id: string) => id.replace(/^id-/, '');

// Prismic game ids may still carry the legacy `id-` prefix from the old platform,
// so match with or without it until the content is cleaned up
export const findGameMetaData = (metaData: SkillGamesConfigDocument | undefined, gameId: string) =>
    metaData?.data.games.find(item => stripLegacyPrefix(item.id ?? '') === stripLegacyPrefix(gameId));

export const useArcadeGamesMetaData = () => {
    const productConfig = useActiveProductConfig();
    const arcadeConfigUid = productConfig?.arcadeConfigUid;
    const staleTime = usePrismicStaleTime();

    return useQuery({
        queryKey: ['arcade-games-meta-data', arcadeConfigUid],
        queryFn: () => {
            if (!arcadeConfigUid) {
                return undefined;
            }

            return prismicClient.getByUID<SkillGamesConfigDocument>('skill_games_config', arcadeConfigUid);
        },
        staleTime,
        enabled: !!arcadeConfigUid,
    });
};

export const useArcadeGames = () => {
    const { data: arcadeGames, isLoading: isArcadeGamesLoading } = useArcadeGamesQuery();
    const { data: metaData, isLoading: isMetaDataLoading } = useArcadeGamesMetaData();
    const jurisdiction = useJurisdictionStore(state => state.jurisdiction);

    const isLoading = isArcadeGamesLoading || isMetaDataLoading;

    const data = useMemo(() => {
        return (
            arcadeGames?.data
                // merge arcade games data from API and prismic
                ?.map(game => {
                    const gameFound = findGameMetaData(metaData, game.game_id);
                    return {
                        ...game,
                        enabled: gameFound?.enabled,
                        name: gameFound?.name ?? game.name,
                        order: gameFound?.order,
                        ineligible_states: gameFound?.ineligible_states?.split(',') ?? [], // ineligible states is a comma separated list of states
                        metadata: {
                            lobby_image: gameFound?.lobby_image,
                            banner: gameFound?.banner,
                            video: gameFound?.video,
                            how_to_play_uid: gameFound?.how_to_play,
                            card_bg_color: gameFound?.card_bg_color,
                            video_url: gameFound?.video_url,
                        },
                    };
                })
                // remove disabled games
                .filter(
                    game =>
                        game.active && game.enabled && (!jurisdiction || !game.ineligible_states.includes(jurisdiction))
                )
                // sort by order from prismic
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                // map fields to the expected format
                .map(game => {
                    const banner = isMediaFilled(game.metadata.banner) ? game.metadata.banner.url : undefined;
                    const lobby_image = isMediaFilled(game.metadata.lobby_image)
                        ? game.metadata.lobby_image.url
                        : undefined;
                    const howToPlayUid = isDocumentFilled(game.metadata.how_to_play_uid)
                        ? game.metadata.how_to_play_uid.uid
                        : undefined;
                    const card_bg_color = game.metadata.card_bg_color ? String(game.metadata.card_bg_color) : undefined;
                    const video = game.metadata.video_url ?? undefined;
                    return {
                        ...game,
                        metadata: {
                            banner,
                            video,
                            lobby_image,
                            card_bg_color,
                            how_to_play_uid: howToPlayUid,
                        },
                    };
                })
        );
    }, [arcadeGames, metaData, jurisdiction]);

    return { isLoading, data };
};
